import { useState } from "react";
import { useAuthStore } from "../state/authStore";
import { CorrectEmailDialog } from "./CorrectEmailDialog";

type ResendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent" }
  | { kind: "rate-limited" }
  | { kind: "error"; message: string };

/**
 * Soft-nag banner shown to users whose email_verified_at is null. Visible on
 * every protected route via the App header. Two actions:
 * - "Erneut senden" (resend the verify mail to the current address)
 * - "E-Mail korrigieren" (open dialog to fix a typo) — issue #68
 * We don't block any app usage; the banner is a reminder so users don't
 * lock themselves out by typoing their email at registration (issue #64).
 */
export function VerifyEmailBanner(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const requestVerifyMail = useAuthStore((s) => s.requestVerifyMail);
  const [state, setState] = useState<ResendState>({ kind: "idle" });
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!user || user.email_verified_at) return null;

  async function onResend() {
    setState({ kind: "sending" });
    const result = await requestVerifyMail();
    if (result.ok) {
      setState({ kind: "sent" });
      return;
    }
    if (result.reason === "rate_limited") {
      setState({ kind: "rate-limited" });
      return;
    }
    setState({
      kind: "error",
      message:
        result.reason === "network"
          ? "Keine Verbindung. Versuch's nochmal."
          : "Versand fehlgeschlagen. Bitte später erneut.",
    });
  }

  function onChanged(newEmail: string) {
    // The dialog already updated user.email via authStore; reset banner state
    // so a stale "Erneut senden" disabled flag doesn't carry over and tell
    // the user "neue Mail unterwegs" keyed to the new address.
    setState({ kind: "sent" });
    void newEmail;
  }

  const note = (() => {
    switch (state.kind) {
      case "sent":
        return "Neue Bestätigungsmail unterwegs. Schau gleich in dein Postfach.";
      case "rate-limited":
        return "Zu viele Anfragen. Bitte später erneut.";
      case "error":
        return state.message;
      default:
        return null;
    }
  })();

  return (
    <>
      <div
        data-testid="verify-email-banner"
        role="status"
        className="bg-warning-soft border-b border-warning/40 px-4 py-2.5 text-fg-soft text-sm flex flex-wrap items-center gap-2 sm:gap-3"
      >
        <span aria-hidden className="text-warning text-base leading-none">!</span>
        <span className="flex-1 min-w-[16ch] text-fg-soft">
          Bitte bestätige deine E-Mail-Adresse <strong>{user.email}</strong> — sonst kannst du dein Passwort später nicht zurücksetzen.
        </span>
        <button
          type="button"
          onClick={() => void onResend()}
          disabled={state.kind === "sending" || state.kind === "sent" || state.kind === "rate-limited"}
          data-testid="verify-email-resend"
          className="min-h-[36px] px-3 py-1 rounded-md bg-accent text-accent-on text-xs font-semibold hover:bg-accent-hover disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
        >
          {state.kind === "sending" ? "…" : "Erneut senden"}
        </button>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          data-testid="verify-email-correct"
          className="min-h-[36px] px-3 py-1 rounded-md bg-bg/40 border border-outline-soft text-fg-soft text-xs font-semibold hover:text-fg hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
        >
          E-Mail korrigieren
        </button>
        {note && (
          <p
            data-testid="verify-email-banner-note"
            className="w-full text-xs text-fg-muted mt-1"
          >
            {note}
          </p>
        )}
      </div>

      <CorrectEmailDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={onChanged} />
    </>
  );
}
